import { call, takeLatest, select, put } from 'redux-saga/effects';
import {
  CREATE_TASK_REQUEST,
  createTaskFailureAction,
  CreateTaskRequestAction,
  createTaskSuccessAction,
} from '../actions';
import { enqueueNotification } from 'store/modules/ui';
import { makeTaskManagementApi } from 'services/TaskManagementApi/TaskManagementApi';
import { ICreateTaskRequest, ITaskAPI } from 'services/TaskManagementApi/types/TaskManagementResponse';
import { duplicatedReminderIdsSelector, listTaskRemindersSelector } from '../selectors';
import { AxiosResponse } from 'axios';
import { TASK_ERROR_MESSAGES } from '../helpers';

export function* createTaskSaga(action: CreateTaskRequestAction) {
  try {
    const duplicatedReminders = yield select(duplicatedReminderIdsSelector);
    if (duplicatedReminders?.length) {
      yield put(createTaskFailureAction('Check duplicated reminders'));
      return;
    }

    const taskReminders = yield select(listTaskRemindersSelector);
    const adjustedReminders = taskReminders.map(item => ({
      channel: item.channel,
      offsetUnit: item.offsetUnit,
      offset: -Math.abs(item.offset),
    }));

    const taskToCreate: ICreateTaskRequest = {
      assigneeId: (!action.task.assigneeId || action.task.assigneeId === 'all') ? null : action.task.assigneeId,
      reviewerId: action.task.reviewerId || null,
      bookingHumanReadableId: action.task.bookingRef,
      categoryId: action.task.category ?? '',
      deadline: action.task.endDate + 'T' + action.task.endTime + ':00Z',
      description: action.task.description,
      priority: action.task.priority,
      reminders: adjustedReminders,
      startBy: !action.task.startByDate?.length ? null : action.task.startByDate + 'T00:00:00Z',
      status: action.task.status,
      subject: action.task.subject,
      teamId: action.task.teamId ?? '',
      crmDirectoryEntryId: action.task.crmDirectoryEntryId || null,
      crmDirectoryEntryFullName: action.task.crmDirectoryEntryFullName || null,
      restrictedToTeamIds: action.task.restrictedToTeamIds ?? [],
    };

    const taskManagementApi = makeTaskManagementApi();
    const createTaskResponse: AxiosResponse<ITaskAPI> = yield call(taskManagementApi.createTask, taskToCreate);
    yield put(createTaskSuccessAction());
    yield put(enqueueNotification({ message: 'New task created successfully.', options: { variant: 'success' } }));
    yield call(action.history.push, `/tasks/${createTaskResponse.data.id}/edit`);
  } catch (e) {
    yield put(createTaskFailureAction(e));
    const errors: { message: string; errorCode: string }[] = e?.response?.data?.errors ?? [];
    let isErrorDisplayed = false;
    for (const error of errors) {
      const errorMessage = TASK_ERROR_MESSAGES[error.errorCode];
      if (errorMessage) {
        isErrorDisplayed = true;
        yield put(enqueueNotification({ message: errorMessage, options: { variant: 'error' } }));
      }
    }
    if (!isErrorDisplayed) {
      yield put(enqueueNotification({ message: 'Error creating task', options: { variant: 'error' } }));
    }
  }
}

export function* watchCreateTaskSaga() {
  yield takeLatest([CREATE_TASK_REQUEST], createTaskSaga);
}
